/**
 * Exchange Service
 *
 * Manages the card exchange system - finding matches between users
 * who have cards others want and want cards others have.
 */

import { db } from './firebase.js';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  query,
  where,
  orderBy,
  limit,
  serverTimestamp,
  arrayUnion,
  arrayRemove,
  Timestamp
} from 'firebase/firestore';
import { ExchangeProfile, TradeMatch } from '../types/collection.ts';
import { getCardsForTrade } from './collectionService.ts';

/**
 * Get or create exchange profile for a user
 */
export async function getExchangeProfile(uid: string): Promise<ExchangeProfile | null> {
  if (!db) return null;

  try {
    const ref = doc(db, 'exchangeProfiles', uid);
    const snap = await getDoc(ref);

    if (snap.exists()) {
      return snap.data() as ExchangeProfile;
    }

    return null;
  } catch (error) {
    console.error('Failed to get exchange profile:', error);
    return null;
  }
}

/**
 * Create or update exchange profile
 */
export async function updateExchangeProfile(
  uid: string,
  data: Partial<ExchangeProfile>
): Promise<void> {
  if (!db) throw new Error('Firebase not initialized');

  const ref = doc(db, 'exchangeProfiles', uid);
  await setDoc(ref, {
    ...data,
    uid,
    lastActive: serverTimestamp(),
  }, { merge: true });
}

/**
 * Sync for-trade cards from collection to exchange profile
 */
export async function syncForTradeCards(uid: string): Promise<number> {
  if (!db) throw new Error('Firebase not initialized');

  const forTradeCards = await getCardsForTrade(uid);
  const cardIds = forTradeCards.map(c => c.cardId);

  const ref = doc(db, 'exchangeProfiles', uid);
  await updateDoc(ref, {
    forTrade: cardIds,
    lastActive: serverTimestamp(),
  });

  return cardIds.length;
}

/**
 * Update seeking list (wanted cards for trading)
 */
export async function updateSeekingList(uid: string, cardIds: string[]): Promise<void> {
  if (!db) throw new Error('Firebase not initialized');

  const ref = doc(db, 'exchangeProfiles', uid);
  await updateDoc(ref, {
    seeking: cardIds,
    lastActive: serverTimestamp(),
  });
}

/**
 * Add card to seeking list
 */
export async function addToSeekingList(uid: string, cardId: string): Promise<void> {
  if (!db) throw new Error('Firebase not initialized');

  const ref = doc(db, 'exchangeProfiles', uid);
  await updateDoc(ref, {
    seeking: arrayUnion(cardId),
    lastActive: serverTimestamp(),
  });
}

/**
 * Remove card from seeking list
 */
export async function removeFromSeekingList(uid: string, cardId: string): Promise<void> {
  if (!db) throw new Error('Firebase not initialized');

  const ref = doc(db, 'exchangeProfiles', uid);
  await updateDoc(ref, {
    seeking: arrayRemove(cardId),
    lastActive: serverTimestamp(),
  });
}

/**
 * Find potential trade matches
 *
 * Algorithm:
 * 1. Get user's wanted list (seeking)
 * 2. Find profiles that have those cards for trade
 * 3. Check if user has cards they're seeking
 * 4. Calculate mutual match score
 * 5. Return sorted matches
 */
export async function findTradeMatches(
  uid: string,
  options?: {
    limit?: number;
    mutualOnly?: boolean;
  }
): Promise<TradeMatch[]> {
  if (!db) return [];

  const maxResults = options?.limit || 20;
  const mutualOnly = options?.mutualOnly ?? true;

  try {
    // Get current user's profile
    const userProfile = await getExchangeProfile(uid);
    if (!userProfile) return [];

    const userSeeking = new Set(userProfile.seeking || []);
    const userForTrade = new Set(userProfile.forTrade || []);

    if (userSeeking.size === 0) return [];

    // Query profiles that have cards user is seeking
    // Note: Firestore has limitations on array-contains queries,
    // so we need to do some client-side filtering
    const profilesRef = collection(db, 'exchangeProfiles');
    const q = query(
      profilesRef,
      where('lastActive', '>', new Timestamp(Date.now() / 1000 - 30 * 24 * 60 * 60, 0)), // Active in last 30 days
      orderBy('lastActive', 'desc'),
      limit(100) // Get more profiles for client-side filtering
    );

    const snap = await getDocs(q);
    const matches: TradeMatch[] = [];

    for (const doc of snap.docs) {
      if (doc.id === uid) continue; // Skip self

      const profile = doc.data() as ExchangeProfile;
      const theirForTrade = new Set(profile.forTrade || []);
      const theirSeeking = new Set(profile.seeking || []);

      // Find cards they have that we want
      const theyHaveYouWant: string[] = [];
      for (const cardId of userSeeking) {
        if (theirForTrade.has(cardId)) {
          theyHaveYouWant.push(cardId);
        }
      }

      // Find cards we have that they want
      const youHaveTheyWant: string[] = [];
      for (const cardId of theirSeeking) {
        if (userForTrade.has(cardId)) {
          youHaveTheyWant.push(cardId);
        }
      }

      // Skip if no matches
      if (theyHaveYouWant.length === 0) continue;

      // Skip if mutual only and no mutual match
      if (mutualOnly && youHaveTheyWant.length === 0) continue;

      // Calculate match score (higher is better)
      // Factors: number of mutual matches, mutual benefit, recency
      const score =
        (theyHaveYouWant.length * 10) +
        (youHaveTheyWant.length * 15) + // Mutual matches weighted higher
        (Math.min(theyHaveYouWant.length, youHaveTheyWant.length) * 20); // Balanced trades bonus

      matches.push({
        user: profile,
        theyHaveYouWant,
        youHaveTheyWant,
        score,
      });
    }

    // Sort by score descending
    matches.sort((a, b) => b.score - a.score);

    return matches.slice(0, maxResults);
  } catch (error) {
    console.error('Failed to find matches:', error);
    return [];
  }
}

/**
 * Get users who have a specific card for trade
 */
export async function findUsersWithCard(cardId: string): Promise<ExchangeProfile[]> {
  if (!db) return [];

  try {
    const profilesRef = collection(db, 'exchangeProfiles');
    const q = query(
      profilesRef,
      where('forTrade', 'array-contains', cardId),
      limit(20)
    );

    const snap = await getDocs(q);
    return snap.docs.map(doc => doc.data() as ExchangeProfile);
  } catch (error) {
    console.error('Failed to find users with card:', error);
    return [];
  }
}

/**
 * Get users seeking a specific card
 */
export async function findUsersSeekingCard(cardId: string): Promise<ExchangeProfile[]> {
  if (!db) return [];

  try {
    const profilesRef = collection(db, 'exchangeProfiles');
    const q = query(
      profilesRef,
      where('seeking', 'array-contains', cardId),
      limit(20)
    );

    const snap = await getDocs(q);
    return snap.docs.map(doc => doc.data() as ExchangeProfile);
  } catch (error) {
    console.error('Failed to find users seeking card:', error);
    return [];
  }
}

/**
 * Initialize exchange profile for new user
 */
export async function initializeExchangeProfile(
  uid: string,
  handle: string
): Promise<void> {
  const existing = await getExchangeProfile(uid);
  if (existing) return;

  await updateExchangeProfile(uid, {
    uid,
    handle,
    forTrade: [],
    seeking: [],
    preferences: {
      shippingAvailable: false,
      localOnly: false,
    },
  });
}
